/* eslint-disable @next/next/no-img-element */
import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { Main as MainLayout } from 'layouts'
import styles from 'pagestyles/financing.module.scss'
import { RootStore } from 'stores/index'
import { Img, ImgBackground, Source } from 'components/basic/Img'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import { Button } from 'components/WebEv/Buttons'
import { AffirmLogoPurple, AffirmLogoPurpleBlackText } from 'components/_const_assets/affirmLogo'
import { ScrollSequence } from 'components/ScrollSequence'
import { ClientOnly } from 'components/ClientOnly'
import { MembersEveryDay } from 'components/WebEv/SleepOS2023/MembersEveryDay'
import { AffirmButton } from 'components/Financing/Financing'
import { KlarnaLogo } from 'components/_const_assets/klarnaLogo'

const bgSet: Source[] = [
	{
		src: 'https://eightsleep.imgix.net/financing_2024_hero_mobile.jpg?v=1706259692',
		mediaQuery: '(max-width: 1023px)',
	},
	{
		src: 'https://eightsleep.imgix.net/financing_2024_hero_desktop.jpg?v=1706259693',
		mediaQuery: '(min-width: 1024px)',
	},
]

interface FinancingPageProps {
	rootStore: RootStore
}

const FinancingPage = (props: FinancingPageProps) => {
	const { priceStore, settingsStore } = props.rootStore
	const { isUS, isCanada, isUK, isEULegacy } = settingsStore
	const coverMonthlyPrice = priceStore.coverMonthlyPriceFormatted
	const coverMaxMonths = priceStore.coverMaxMonthsAtZeroInterest
	const coverKlarnaMonthlyPrice = priceStore.coverKlarnaMonthlyPriceFormatted
	const coverKlarnaMaxMonths = priceStore.coverKlarnaMaxMonthsAtZeroInterest

	const FinancingTable = () => {
		if (isUS)
			return (
				<>
					<div className={styles.financing_table}>
						<p>
							$0
							<small>down</small>
						</p>
						<p>
							0%
							<small>apr</small>
						</p>
						<p>
							{coverMaxMonths}
							<small>months</small>
						</p>
					</div>
					<div className={styles.affirm_logo}>
						Pay {coverMonthlyPrice}/month* over
						<br className="hideMdUp" /> 36 months with <AffirmLogoPurpleBlackText />
					</div>
				</>
			)
		else if (isCanada) {
			return (
				<>
					<div className={styles.financing_table}>
						<p>
							$0
							<small>down</small>
						</p>
						<p>
							10 - 30%
							<small>apr</small>
						</p>
						<p>
							{coverMaxMonths}
							<small>months</small>
						</p>
					</div>
					<div className={styles.affirm_logo}>
						Pay {coverMonthlyPrice}/month* over
						<br className="hideMdUp" /> 36 months with <AffirmLogoPurpleBlackText />
					</div>
				</>
			)
		} else if (isUK || isEULegacy) {
			return (
				<>
					<div className={`${styles.financing_table} ${styles.mini_table}`}>
						<p>
							{coverKlarnaMonthlyPrice}
							<small>each month</small>
						</p>
						<p>
							{coverKlarnaMaxMonths}
							<small>payments</small>
						</p>
					</div>
					<div className={styles.affirm_logo2}>
						Pay in {coverKlarnaMaxMonths} payments of {coverKlarnaMonthlyPrice} with <KlarnaLogo color="#000" />
					</div>
				</>
			)
		}
	}

	return (
		<MainLayout>
			<section>
				<ImgBackground
					src={bgSet[0].src}
					alt={''}
					sources={bgSet}
					className={styles.hero_container}
				>
					<div className={`inner-paddings-x ${styles.hero_content}`}>
						<h1>
							Unlock perfect sleep now.
							<br />
							Pay over time.
						</h1>
						<p>
							Own the Pod for as low as {isUK || isEULegacy ? coverKlarnaMonthlyPrice : coverMonthlyPrice}/month* with{' '}
							{isUS || isCanada ? (
								<span className={styles.affirm_wrapper}>
									<AffirmLogoPurple />
								</span>
							) : (
								<span className={styles.svg_wrapper}>
									<KlarnaLogo color="#fff" />
								</span>
							)}
						</p>
						<AnchorLink
							href="#about"
							offset={75}
						>
							<Button.Secondary
								id={'financing-learn-more'}
								className={styles.button_link}
							>
								Learn more
							</Button.Secondary>
						</AnchorLink>
					</div>
				</ImgBackground>
			</section>

			<section
				className="inner-paddings-x inner-paddings-y bg-white"
				id="about"
			>
				<div className={styles.about_container}>
					<h2>No hidden fees, upgrading your sleep without the worry</h2>
					<div className={styles.financing_table_wrapper}>{FinancingTable()}</div>
					<div>
						<Button.Primary
							id={'financing-lp-shop-now'}
							className={styles.cta}
							href="/product/pod-cover/"
						>
							Shop now
						</Button.Primary>
					</div>
				</div>
			</section>

			<section className={'inner-paddings-x inner-paddings-y bg-black'}>
				<div className={`max-1440 ${styles.zigzag}`}>
					<h2>Finance your Pod in three steps</h2>
					<div className={styles.zigzag_container}>
						<div className={styles.zigzag_item}>
							<div className={styles.zigzag_content}>
								<small>Step 1</small>
								<p>Add to cart</p>
								<span>Select ‘Pay Over time’ at checkout to begin financing the Pod.</span>
							</div>
							<Img
								src={
									isUS || isCanada
										? 'https://eightsleep.imgix.net/finance_img1_b8a8f817-5c1f-4673-8409-e8512bdf198d.jpg?v=1706278528'
										: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/finance_img1_international.jpg'
								}
								alt=""
							/>
						</div>
						<div className={styles.zigzag_item}>
							<Img
								src={
									isUS || isCanada
										? 'https://eightsleep.imgix.net/finance_img2_098751b5-8d52-4a37-b1d2-20dd094a3896.jpg?v=1706278529'
										: 'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/finance_img2_international.jpg'
								}
								alt=""
							/>
							<div className={`${styles.zigzag_content} ${styles.reverse}`}>
								<small>Step 2</small>
								<p>Pick a payment plan</p>
								<span>Set up easy, automatic payments from 12 to 36 months.</span>
							</div>
						</div>
						<div className={styles.zigzag_item}>
							<div className={styles.zigzag_content}>
								<small>Step 3</small>
								<p>Start your sleep fitness journey</p>
								<span>Sleep deeper everyday with automatic monthly payments.</span>
							</div>
							<Img
								src={'https://eight-eightsleep-react.s3.us-east-2.amazonaws.com/assets/financing_step_3.jpeg'}
								alt=""
								dprHeight={1000}
							/>
						</div>
					</div>
					{isUS || isCanada ? (
						<div className={styles.qualify}>
							<div>
								<h3>See if you qualify for financing. Get an answer in minutes.</h3>
								<div>
									<AffirmButton
										className={styles.financing_link}
										text="Pre-qualify now"
										noArrow
									/>
								</div>
								<small>Get a real-time decision with just 5 pieces of info.</small>
							</div>
						</div>
					) : (
						<></>
					)}
				</div>
			</section>

			<section
				id={'members-every-day'}
				className={'bg-white'}
			>
				<MembersEveryDay title="Thousands of others, powered by the Pod" />
			</section>

			<section className="inner-paddings-x bg-black">
				<ClientOnly>
					<ScrollSequence
						title="Get the Pod"
						subtitle="The best in sleep fitness technology"
						markers={false}
						scrubSeconds={0}
						length={600}
					/>
				</ClientOnly>
			</section>

			{isUS || isCanada ? (
				<section className="inner-paddings-x inner-paddings-y bg-black">
					<p className={styles.disclaimer}>
						*Excludes tax. Monthly prices displayed are based on a queen-size Pod Cover price of $2,245. Enter a few details for a real-time decision. ²Your rate will be 0% APR or 10-30% APR based on
						credit, and is subject to eligibility check and approval. Options depend on your purchase amount and credit score.
					</p>
				</section>
			) : (
				<></>
			)}
		</MainLayout>
	)
}

export default inject('rootStore')(observer(FinancingPage))
